import { Suspense, lazy, type ReactNode } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import { PermissionProvider } from "./lib/permissions/PermissionProvider";
import { useCrossWindowSync } from "./lib/useCrossWindowSync";
import { ProtectedRoute } from "./shell/ProtectedRoute";
import { Layout } from "./shell/Layout";
import { LoginPage } from "./features/auth/LoginPage";
import { SetPasswordView } from "./features/auth/SetPasswordView";
import { TodayView } from "./features/today/TodayView";
import { CustomerSearchView } from "./features/customer-360/CustomerSearchView";
import { CustomerDetailView } from "./features/customer-360/CustomerDetailView";
import { BoardView } from "./features/board/BoardView";
import { DispatchDetailView } from "./features/dispatch-detail/DispatchDetailView";
import { InvoiceDetailView } from "./features/invoice-detail/InvoiceDetailView";
import { InvoicesView } from "./features/invoice/InvoicesView";
import { AutomationsView } from "./features/comms/AutomationsView";
import { AgreementsWorklistView } from "./features/agreements/AgreementsWorklistView";
import { TuneUpPlannerView } from "./features/agreements/TuneUpPlannerView";
import { TechApp } from "./features/mobile/TechApp";
import { InboxView } from "./features/inbox/InboxView";
import { MarketingView } from "./features/marketing/MarketingView";
import { ReportsView } from "./features/reports/ReportsView";
import { ItemsView } from "./features/items/ItemsView";
import { VendorsView } from "./features/vendors/VendorsView";
import { PurchaseOrdersView } from "./features/purchase-orders/PurchaseOrdersView";
import { JobsView, JobWorkspace } from "./features/jobs/JobsView";
import { WarehouseView } from "./features/jobs/WarehouseView";
import { InvoicePrintView } from "./features/jobs/InvoicePrintView";
import { InstallOrderPrintView, RebateTestPrintView } from "./features/jobs/InstallPaperPrint";
import { PickSheetPrintView } from "./features/jobs/PickSheetPrint";
import { QuoteBuilder } from "./features/quotes/QuoteBuilder";
import { QuotesListView } from "./features/quotes/QuotesListView";
import { SettingsView } from "./features/settings/SettingsView";
import { TeamView } from "./features/team/TeamView";
import { TvScoreboard } from "./features/team/ScoreboardView";
import { BoardMapWindow } from "./features/board/BoardMapWindow";
import { OncallPrintView } from "./features/team/OncallPrintView";
import { PartsPrintView } from "./features/parts/PartsPrintView";

/** TYPE-SCALE-02: the working pages read at desk size (the JOB-READ-01 scale in
 *  index.css, scoped by .readable). display: contents — no layout box. */
function Readable({ children }: { children: ReactNode }) {
  return <div className="readable">{children}</div>;
}

// TMPL-02 spike — LAZY so the heavy editor bundles (easy-email + Arco CSS)
// load only when /editor-spike is visited, never with the main app.
const EditorSpike = lazy(() => import("./features/templates-spike/EditorSpike"));

// EMAIL-MKTG — GrapesJS drag-and-drop email builder, lazy so grapesjs + its CSS
// load only when /email-builder is visited.
const EmailBuilder = lazy(() => import("./features/email-builder/EmailBuilder"));

// PORTAL — public, passwordless customer-facing pages reached from email links.
const CustomerPortal = lazy(() => import("./features/portal/CustomerPortal").then((m) => ({ default: m.CustomerPortal })));
const UnsubscribePage = lazy(() => import("./features/portal/UnsubscribePage").then((m) => ({ default: m.UnsubscribePage })));
const PortalLanding = lazy(() => import("./features/portal/PortalLanding").then((m) => ({ default: m.PortalLanding })));
const ProposalView = lazy(() => import("./features/quotes/ProposalView").then((m) => ({ default: m.ProposalView })));
// FIELD-PAY-01: the installer's Klarna QR opens this on the customer's phone.
const JobPayPage = lazy(() => import("./features/portal/JobPayPage").then((m) => ({ default: m.JobPayPage })));
// LETTER-PAGE-01: the public per-city inspection letter the close-out email links.
const CityLetterPage = lazy(() => import("./features/portal/CityLetterPage").then((m) => ({ default: m.CityLetterPage })));

export default function App() {
  // Two-monitor dispatcher sync: refetch when ANOTHER window clears/schedules a
  // ticket (Seth 2026-06-24). Harmless on the portal host (no internal queries).
  useCrossWindowSync();
  // my.sedgwickheating.com (or portal.) = the CUSTOMER portal ONLY. The internal
  // Pegasus app lives on app.sedgwickheating.com / localhost. A customer must
  // never land on the dispatcher login, so the portal host renders only the
  // public routes + a friendly landing for the bare root.
  const portalHost = typeof window !== "undefined" && /^(my|portal)\./i.test(window.location.hostname);
  if (portalHost) {
    return (
      <Suspense fallback={<div className="flex h-screen items-center justify-center text-sm text-slate-400" />}>
        <Routes>
          <Route path="/portal" element={<CustomerPortal />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/proposal" element={<ProposalView />} />
          <Route path="/pay/:token" element={<JobPayPage />} />
          <Route path="/letter/:city" element={<CityLetterPage />} />
          <Route path="*" element={<PortalLanding />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <AuthProvider>
      <PermissionProvider>
        <Routes>
        {/* Public route */}
        <Route path="/login" element={<LoginPage />} />
        {/* Invite / password-reset landing — the token in the URL is the auth. */}
        <Route path="/set-password" element={<SetPasswordView />} />

        {/* Public customer-facing pages — token-secured (?t=), no login. */}
        <Route path="/portal" element={<Suspense fallback={<div />}><CustomerPortal /></Suspense>} />
        <Route path="/unsubscribe" element={<Suspense fallback={<div />}><UnsubscribePage /></Suspense>} />
        <Route path="/proposal" element={<Suspense fallback={<div />}><ProposalView /></Suspense>} />
        <Route path="/pay/:token" element={<Suspense fallback={<div />}><JobPayPage /></Suspense>} />
        <Route path="/letter/:city" element={<Suspense fallback={<div />}><CityLetterPage /></Suspense>} />

        {/* Chrome-free break-room scoreboard (auth-gated; cast/AirPlay the tab to the shop TV). */}
        <Route path="/team/tv" element={<ProtectedRoute><TvScoreboard /></ProtectedRoute>} />
        {/* ONCALL-SCHED-01c: the on-call sheet CEAS gets — chrome-free, one month per page (Print → Save as PDF). */}
        <Route path="/team/oncall/print" element={<ProtectedRoute><OncallPrintView /></ProtectedRoute>} />
        {/* PARTS-PRINT-01: the morning pick sheet — chrome-free, one block per tech (Print → paper or PDF). */}
        <Route path="/parts/print" element={<ProtectedRoute><PartsPrintView /></ProtectedRoute>} />
        {/* BOARD-MAP-STATES: the board map in its own window (second monitor) —
            fed live by the board over a BroadcastChannel. */}
        <Route path="/board/map" element={<ProtectedRoute><BoardMapWindow /></ProtectedRoute>} />

        {/* PEGASUS FIELD (FIELD-M0) — the phone-first tech app. Chrome-free like
            /team/tv: its own shell, bottom nav, no desktop sidebar. */}
        <Route path="/m" element={<ProtectedRoute><TechApp /></ProtectedRoute>} />

        {/* Chrome-free printable customer invoice (auth-gated; opens in its own tab → Save as PDF). */}
        <Route path="/jobs/:jobId/invoice" element={<ProtectedRoute><InvoicePrintView /></ProtectedRoute>} />
        {/* INSTALL-CHECK-01 / REBATE-TEST-01: the job packet's Install Order + Rebate Test Form, filled in. */}
        <Route path="/jobs/:jobId/install-order" element={<ProtectedRoute><InstallOrderPrintView /></ProtectedRoute>} />
        <Route path="/jobs/:jobId/rebate-test" element={<ProtectedRoute><RebateTestPrintView /></ProtectedRoute>} />
        {/* MATERIAL-REQ-01: the per-job pick sheet + the cut-out label with a QR code. */}
        <Route path="/jobs/:jobId/pick-sheet" element={<ProtectedRoute><PickSheetPrintView /></ProtectedRoute>} />
        {/* Unified invoice by id — install or service, the one invoice document. */}
        <Route path="/invoices/:invoiceId" element={<ProtectedRoute><InvoicePrintView /></ProtectedRoute>} />
        {/* ESC historical invoice rendered in the customer document (by its real ESC number). */}
        <Route path="/invoices/esc/:escNo" element={<ProtectedRoute><InvoicePrintView /></ProtectedRoute>} />

        {/* Protected routes - everything inside ProtectedRoute requires auth */}
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/customers" replace />} />
          <Route path="/today" element={<TodayView />} />
          <Route path="/board" element={<BoardView />} />
          <Route path="/dispatch/:dispatch" element={<Readable><DispatchDetailView /></Readable>} />
          <Route path="/invoice/:invoice" element={<Readable><InvoiceDetailView /></Readable>} />
          <Route path="/invoices" element={<Readable><InvoicesView /></Readable>} />
          <Route path="/automations" element={<AutomationsView />} />
          <Route path="/agreements" element={<AgreementsWorklistView />} />
          {/* PLANNER-01: Route & Batch rebuilt + renamed. The old /agreements/route
              URL keeps working (bookmarks, segment tabs) — same workspace. */}
          <Route path="/agreements/planner" element={<TuneUpPlannerView />} />
          <Route path="/agreements/route" element={<TuneUpPlannerView />} />
          <Route path="/customers" element={<CustomerSearchView />} />
          <Route
            path="/customers/:custno"
            element={<CustomerDetailView />}
          />
          <Route path="/inbox" element={<InboxView />} />
          <Route path="/marketing" element={<MarketingView />} />
          <Route path="/reports" element={<ReportsView />} />
          <Route path="/items" element={<ItemsView />} />
          <Route path="/vendors" element={<VendorsView />} />
          <Route path="/purchase-orders" element={<PurchaseOrdersView />} />
          <Route path="/jobs" element={<JobsView />} />
          <Route path="/jobs/:jobId" element={<JobWorkspace />} />
          <Route path="/warehouse" element={<WarehouseView />} />
          <Route path="/team" element={<TeamView />} />
          <Route path="/quotes" element={<Readable><QuotesListView /></Readable>} />
          <Route path="/quotes/:id" element={<Readable><QuoteBuilder /></Readable>} />
          <Route path="/settings" element={<Readable><SettingsView /></Readable>} />
          <Route
            path="/editor-spike"
            element={
              <Suspense
                fallback={
                  <div className="flex h-full items-center justify-center text-sm text-slate-400">
                    Loading the editor spike…
                  </div>
                }
              >
                <EditorSpike />
              </Suspense>
            }
          />
          <Route
            path="/email-builder"
            element={
              <Suspense
                fallback={
                  <div className="flex h-full items-center justify-center text-sm text-slate-400">
                    Loading the email builder…
                  </div>
                }
              >
                <EmailBuilder />
              </Suspense>
            }
          />
        </Route>

          {/* Anything else -> redirect home (which redirects to login if needed) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </PermissionProvider>
    </AuthProvider>
  );
}
